import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	input,
	NgZone,
	OnDestroy,
	Output,
	signal,
} from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";
import { animationFrames, Subscription } from "rxjs";
import { distinctUntilChanged, map, takeWhile } from "rxjs/operators";

@Component({
	selector: "countdown",
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: "./countdown.component.scss",
	template: `
			@if (fullView()) {
				<div class="cd-container">
					<div class="d-flex d-lg-block">
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ days() }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.days"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(hours()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.hours"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(minutes()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.mins"|translate}}</span></span>
					<span class="cd-value py-2 py-lg-0 ps-2 pe-3 col d-lg-inline-block"><span class="fw-bold text-big text-lightblue">{{ pad(seconds()) }}</span><br><span class="text-xxs text-uppercase cd-label">{{"clock.secs"|translate}}</span></span>
				</div>
			</div>
			} @else {
					{{ pad(minutes()) }}:{{ pad(seconds()) }}
			}
	`,
	imports: [TranslatePipe]
})
export class CountdownComponent implements OnDestroy {
	private zone = inject(NgZone);
	private sub?: Subscription;
	private lastAdjustedTarget = 0;

	fullView = input<boolean>(true);
	deadline = input.required<Date | string | number>();
	/** Delta in ms: (server time - local machine time) */
	timeOffset = input<number>(0);

	private remainingSeconds = signal(0);

	// Calcoli derivati
	days = computed(() => Math.floor(this.remainingSeconds() / 86400));
	hours = computed(() => Math.floor((this.remainingSeconds() % 86400) / 3600));

	minutes = computed(() =>
		this.fullView()
			? Math.floor((this.remainingSeconds() % 3600) / 60)
			: Math.floor(this.remainingSeconds() / 60)
	);

	seconds = computed(() => this.remainingSeconds() % 60);

	@Output() expired = new EventEmitter<void>();

	pad = (val: number): string => val.toString().padStart(2, "0");

	constructor() {
		effect(() => {
			/* if (!this.fullView()) {
				console.warn("deadline", this.deadline(), "now", new Date())
			} */
			const targetMs = new Date(this.deadline()).getTime(),
				offset = this.timeOffset();

			if (!isNaN(targetMs)) {
				// Adjusting target to server/client offset
				// If server time is higher (offset > 0), il target locale deve essere anticipato
				const adjustedTarget = targetMs - offset;

				// Not restarting if total hasn't been changed
				if (adjustedTarget !== this.lastAdjustedTarget) {
					this.lastAdjustedTarget = adjustedTarget;
					this.restart(adjustedTarget);
				}
			}
		});
	}

	private restart(adjustedTarget: number) {
		this.stop(); // Ferma quello precedente
		this.zone.runOutsideAngular(() => {
			this.sub = animationFrames().pipe(
				// Calcolating time offset
				map(() => adjustedTarget - Date.now()),
				// Converting to seconds (minimum 0)
				map(ms => Math.max(Math.floor(ms / 1000), 0)),
				// checking then the second actually changes
				distinctUntilChanged(),
				// ... while value > 0 (emitting 0 thanks to final true)
				takeWhile(sec => sec >= 0, true)
			).subscribe({
				next: sec => {
					this.zone.run(() => {
						this.remainingSeconds.set(sec);
						if (sec === 0) {
							this.expired.emit();
							this.stop();
						}
					});
				},
				error: err => console.error("Countdown Error:", err)
			});
		});
	}

	private stop() {
		this.sub?.unsubscribe();
		this.sub = undefined;
	}

	ngOnDestroy() {
		this.stop();
	}
}